import { computed, onMounted, onBeforeUnmount, useHost, type ComputedRef } from "vue";
import type { ComboboxItemProps } from "./combobox.types";
import type { ListboxItemProps } from "../Listbox/listbox.types";
import { useSharedState } from "composables/useSharedState";
import { useCombobox, registerComboboxItem, unregisterComboboxItem } from "./useCombobox";

export interface UseComboboxItemOptions {
  props: Readonly<ComboboxItemProps>;
}

export interface UseComboboxItemReturn {
  /** Override for ListboxItem highlighted state */
  isHighlighted: ComputedRef<boolean>;
  /** Whether item is selected */
  isSelected: ComputedRef<boolean>;
  /** Whether item is disabled */
  isDisabled: ComputedRef<boolean>;
  /** Whether item is visible (passes filter) */
  isVisible: ComputedRef<boolean>;
  /** Style props from parent combobox */
  color: ComputedRef<ListboxItemProps["color"]>;
  variant: ComputedRef<ListboxItemProps["variant"]>;
  size: ComputedRef<ListboxItemProps["size"]>;
  /** Handle select event from ListboxItem */
  handleSelect: () => void;
}

export const useComboboxItem = (options: UseComboboxItemOptions): UseComboboxItemReturn => {
  const { props } = options;
  const host = useHost();

  // Register in state store for keyboard navigation discovery
  useSharedState(props);

  const itemValue = computed(() => props.value);
  const combobox = useCombobox({ itemValue });

  // Computed state for ListboxItem overrides
  const isHighlighted = computed(() => combobox.isHighlighted.value);
  const isSelected = computed(() => combobox.isSelected.value);
  const isDisabled = computed(() => props.disabled || combobox.isDisabled.value);
  const isVisible = computed(() => combobox.isFiltered.value);

  // Style props - item props take precedence over parent
  const color = computed(() => props.color ?? combobox.comboboxProps.value?.color);
  const variant = computed(() => props.variant ?? combobox.comboboxProps.value?.variant);
  const size = computed(() => props.size ?? combobox.comboboxProps.value?.size);

  // Cache combobox element for registration/unregistration
  let cachedComboboxEl: Element | null = null;

  onMounted(() => {
    cachedComboboxEl = host?.closest("e-combobox") ?? combobox.comboboxElement.value;

    if (props.value !== undefined && cachedComboboxEl) {
      const label = props.label ?? host?.textContent?.trim() ?? String(props.value);
      const textValue = props.textValue ?? label;
      registerComboboxItem(cachedComboboxEl, props.value, label, textValue, props.disabled);
    }
  });

  onBeforeUnmount(() => {
    if (props.value !== undefined && cachedComboboxEl) {
      unregisterComboboxItem(cachedComboboxEl, props.value);
    }
  });

  // Handle select event from ListboxItem
  const handleSelect = () => {
    if (isDisabled.value) return;

    // Toggle selection in parent Combobox
    combobox.toggleSelection(itemValue.value);
  };

  return {
    isHighlighted,
    isSelected,
    isDisabled,
    isVisible,
    color,
    variant,
    size,
    handleSelect,
  };
};
